import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, ShoppingCart, Heart, Star, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { id: 'all', name: 'All', icon: '🛒' },
  { id: 'electronics', name: 'Electronics', icon: '📱' },
  { id: 'fashion', name: 'Fashion', icon: '👟' },
  { id: 'home', name: 'Home', icon: '🏠' },
  { id: 'beauty', name: 'Beauty', icon: '💄' },
  { id: 'sports', name: 'Sports', icon: '⚽' },
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

export default function BuyPage() {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    try {
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory.toLowerCase()}` : '';
      const response = await axios.get(`${API_URL}/api/products${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Products fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    setCart([...cart, product]);
    toast.success(`${product.name} added to cart`);
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AppLayout title="Shop" showSearch>
      <div className="p-4 lg:p-6">
        {/* Search Mobile */}
        <div className="mb-4 md:hidden">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search products..." 
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="mobile-search"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="mb-6">
          <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                  selectedCategory === cat.id
                    ? 'category-pill-active shadow-md'
                    : 'bg-muted hover:bg-muted/80'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <span>{cat.icon}</span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Cart indicator */}
        {cart.length > 0 && (
          <div className="mb-4 p-3 jambo-gradient-subtle rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-cyan-600" />
              <span className="font-medium">{cart.length} items in cart</span>
            </div>
            <Button size="sm" className="rounded-full jambo-gradient text-white">
              Checkout
            </Button>
          </div>
        )}

        {/* Products Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {loading ? (
            Array(8).fill(0).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="aspect-square" />
                <CardContent className="p-3">
                  <Skeleton className="h-4 w-3/4 mb-2" />
                  <Skeleton className="h-5 w-1/2" />
                </CardContent>
              </Card>
            ))
          ) : filteredProducts.length ? (
            filteredProducts.map((product, index) => (
              <Card 
                key={product.id} 
                className={`overflow-hidden product-card group animate-fade-in stagger-${index % 6 + 1}`}
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-square overflow-hidden bg-muted relative">
                  <img 
                    src={product.image_url || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400'}
                    alt={product.name}
                    className="product-image w-full h-full object-cover"
                  />
                  <button 
                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                    data-testid={`wishlist-${product.id}`}
                  >
                    <Heart className="h-4 w-4" />
                  </button>
                  <Badge className="absolute top-2 left-2 bg-cyan-500 text-white">
                    {product.category}
                  </Badge>
                </div>
                <CardContent className="p-3">
                  <p className="font-medium text-sm truncate mb-1">{product.name}</p>
                  <div className="flex items-center gap-1 mb-2">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    <span className="text-xs text-muted-foreground">4.8 (120)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="price-tag text-cyan-600">
                      {formatCurrency(product.price)}
                    </p>
                    <Button 
                      size="sm" 
                      className="h-8 rounded-full bg-gradient-to-r from-cyan-500 to-green-500 text-white"
                      onClick={() => addToCart(product)}
                      data-testid={`add-cart-${product.id}`}
                    >
                      <ShoppingCart className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full py-20 text-center text-muted-foreground">
              <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No products found</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
